import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import "./ContentPane.scss";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import {CourseEditorContext, COURSE_MODES} from "../../../../contexts/CourseEditorContext";
import {ChapterEditor} from "./ChapterEditor/ChapterEditor";

// Create a cache for template components to avoid recreating them
const templateComponentsCache = new Map();

// Memoize the ContentPane component to prevent unnecessary re-renders
export const ContentPanel = ({onChapterComplete, courseCompleted = false}) => {
    const {
        mode = COURSE_MODES.EDIT,
        selectedSection
    } = useContext(CourseEditorContext) || {};
    const { i18n } = useContext(I18nReactContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [contentHeight, setContentHeight] = useState('auto');
    const contentRef = useRef(null);
    const videoRef = useRef(null);
    const [videoCompleted, setVideoCompleted] = useState(false);
    // Track which videos have been completed in this chapter
    const [completedVideos, setCompletedVideos] = useState([]);

    // Adjust content height on window resize
    useEffect(() => {
        const handleResize = () => {
            if (contentRef.current) {
                setContentHeight(`${contentRef.current.clientHeight}px`);
            }
        };

        handleResize(); // Initial calculation
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Handle video completion
    const handleVideoEnded = useCallback((videoId) => {
        console.log('Video ended:', videoId);
        setVideoCompleted(true);

        // Skip if the course is already completed
        if (courseCompleted) {
            console.log('Course already completed, skipping video completion tracking');
            return;
        }

        // Add this video to the completed videos array if it's not already there
        setCompletedVideos(prev => {
            if (!prev.includes(videoId)) {
                return [...prev, videoId];
            }
            return prev;
        });

        // For the 'video' template, we can mark the chapter as completed immediately
        // For the 'advanced' template, we'll check if all videos are completed in the useEffect below
        if (selectedSection?.template === 'video' && onChapterComplete) {
            onChapterComplete();
        }
    }, [onChapterComplete, selectedSection, courseCompleted]);

    // Handle video loaded metadata
    const handleVideoMetadata = useCallback(() => {
        console.log('Video metadata loaded');
        if (contentRef.current) {
            setContentHeight(`${contentRef.current.clientHeight}px`);
        }
    }, []);

    // Render content based on template type
    const renderContent = useMemo(() => {
        if (!selectedSection) {
            return (
                <div className="content-placeholder">
                    {i18n.translate('weezbeeCourse.contentPane.noChapterSelected')}
                </div>
            );
        }

        const { template, templateData = {}, components = [] } = selectedSection;

        // Handle advanced editor template
        if (template === 'advanced') {
            return (
                <div className="content-advanced">
                    <h2>{selectedSection.title}</h2>
                    <div className="advanced-content">
                        {components.length > 0 ? (
                            components.sort((a, b) => a.position - b.position).map(component => {
                                if (component.type === 'text') {
                                    return (
                                        <div key={component.id} className="component-text">
                                            <div className="text-content">
                                                {component.content || <span className="placeholder">{i18n.translate('weezbeeCourse.contentPane.noContent')}</span>}
                                            </div>
                                        </div>
                                    );
                                }
                                // Add more component types here as needed
                                return null;
                            })
                        ) : (
                            <p className="no-components">{i18n.translate('weezbeeCourse.contentPane.noComponents', { defaultValue: 'No content has been added to this chapter yet.' })}</p>
                        )}
                    </div>
                    <div className="complete-button-container">
                        <button
                            className="complete-button"
                            onClick={() => {
                                if (courseCompleted) {
                                    console.log('Course already completed, skipping chapter completion');
                                    return;
                                }
                                onChapterComplete && onChapterComplete();
                            }}
                            disabled={courseCompleted}
                        >
                            {courseCompleted
                                ? i18n.translate('weezbeeCourse.contentPane.courseAlreadyCompleted', { defaultValue: 'Course Completed' })
                                : i18n.translate('weezbeeCourse.contentPane.markAsCompleted')}
                        </button>
                    </div>
                </div>
            );
        }

        // Default content if no template is specified
        if (!template) {
            return (
                <div className="content-default">
                    <h2>{selectedSection.title}</h2>
                    <p>{templateData.content || i18n.translate('weezbeeCourse.contentPane.noContent')}</p>
                </div>
            );
        }

        // Render based on template type
        switch (template) {
            case 'video':
                return (
                    <div className="content-video">
                        <h2>{selectedSection.title}</h2>
                        {templateData.videoUrl ? (
                            <div className="video-container">
                                <video
                                    ref={videoRef}
                                    controls
                                    onEnded={() => handleVideoEnded('main-video')}
                                    onLoadedMetadata={handleVideoMetadata}
                                    className="video-player"
                                >
                                    <source src={templateData.videoUrl} type="video/mp4" />
                                    {i18n.translate('weezbeeCourse.contentPane.videoNotSupported')}
                                </video>
                                {videoCompleted && (
                                    <div className="video-completed-overlay">
                                        <span>{i18n.translate('weezbeeCourse.contentPane.videoCompleted')}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p>{i18n.translate('weezbeeCourse.contentPane.noVideoUrl')}</p>
                        )}
                        {templateData.description && (
                            <div className="video-description">
                                <p>{templateData.description}</p>
                            </div>
                        )}
                    </div>
                );

            case 'text':
                return (
                    <div className="content-text">
                        <h2>{selectedSection.title}</h2>
                        <div className="text-content">
                            {templateData.content ? (
                                <div dangerouslySetInnerHTML={{ __html: templateData.content }} />
                            ) : (
                                <p>{i18n.translate('weezbeeCourse.contentPane.noContent')}</p>
                            )}
                        </div>
                        {!templateData.disableAutoComplete && (
                            <div className="complete-button-container">
                                <button
                                    className="complete-button"
                                    onClick={() => {
                                        if (courseCompleted) {
                                            console.log('Course already completed, skipping chapter completion');
                                            return;
                                        }
                                        onChapterComplete && onChapterComplete();
                                    }}
                                    disabled={courseCompleted}
                                >
                                    {courseCompleted
                                        ? i18n.translate('weezbeeCourse.contentPane.courseAlreadyCompleted', { defaultValue: 'Course Completed' })
                                        : i18n.translate('weezbeeCourse.contentPane.markAsCompleted')}
                                </button>
                            </div>
                        )}
                    </div>
                );

            case 'quiz':
                return (
                    <div className="content-quiz">
                        <h2>{selectedSection.title}</h2>
                        <p>{i18n.translate('weezbeeCourse.contentPane.quizNotImplemented')}</p>
                        {/* Quiz implementation will go here in future */}
                    </div>
                );

            default:
                return (
                    <div className="content-unknown">
                        <h2>{selectedSection.title}</h2>
                        <p>{i18n.translate('weezbeeCourse.contentPane.unknownTemplate', {
                            context: { template }
                        })}</p>
                    </div>
                );
        }
    }, [selectedSection, i18n, handleVideoEnded, handleVideoMetadata, videoCompleted, onChapterComplete, courseCompleted]);

    // Check if all videos in the chapter have been completed
    // This is used for the 'advanced' template where a chapter can have multiple videos
    // Only mark the chapter as completed when all videos have been watched
    useEffect(() => {
        if (courseCompleted) {
            return;
        }

        // Only check for advanced template in view modes (ADMIN_VIEW or USER_VIEW)
        const isViewMode = mode === COURSE_MODES.ADMIN_VIEW || mode === COURSE_MODES.USER_VIEW;
        if (isViewMode && selectedSection?.template === 'advanced' && selectedSection?.components) {
            // Get all video components in the chapter
            const videoComponents = selectedSection.components.filter(component => component.type === 'video');

            // If there are no video components, no need to check
            if (videoComponents.length === 0) return;

            // Check if all video components have been completed
            const allVideosCompleted = videoComponents.every(video =>
                completedVideos.includes(video.id)
            );


            // If all videos have been completed, mark the chapter as completed
            if (allVideosCompleted && onChapterComplete) {
                onChapterComplete();
            }
        }
    }, [completedVideos, selectedSection, mode, onChapterComplete, courseCompleted]);


    return (
        <div className={`ContentPane ${mode === "edit" ? 'edit-mode' : 'view-mode'}`} ref={contentRef}>
            {isLoading ? (
                <div className="loading-container">
                    <LoadingSpinner />
                </div>
            ) : error ? (
                <div className="error-container">
                    <p className="error-message">{error}</p>
                </div>
            ) : (
                <ChapterEditor
                    onVideoComplete={handleVideoEnded}
                    courseCompleted={courseCompleted}
                />
            )}
        </div>
    );
};
